import React, { useRef, useState, useEffect, useCallback } from 'react';
import { compressImageBlob } from './avatarUtils';

interface CropModalProps {
  /** Data URL or Object URL of the selected image */
  imageSrc: string;
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}

const CANVAS_SIZE = 300;   // px – visible canvas
const CROP_SIZE = 256;     // px – square crop zone side length
const OUTPUT_SIZE = 400;   // px – final exported square canvas

/** Clamp image offset so the square crop zone is always fully covered by the image. */
function clampOffset(
  scale: number,
  offset: { x: number; y: number },
  natW: number,
  natH: number,
): { x: number; y: number } {
  const maxX = Math.max((natW * scale) / 2 - CROP_SIZE / 2, 0);
  const maxY = Math.max((natH * scale) / 2 - CROP_SIZE / 2, 0);
  return {
    x: Math.min(Math.max(offset.x, -maxX), maxX),
    y: Math.min(Math.max(offset.y, -maxY), maxY),
  };
}

export default function CropModal({ imageSrc, onConfirm, onCancel }: CropModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [processing, setProcessing] = useState(false);

  // drag state stored in a ref to avoid closure staleness
  const dragRef = useRef<{ active: boolean; startMx: number; startMy: number; startOx: number; startOy: number }>({
    active: false, startMx: 0, startMy: 0, startOx: 0, startOy: 0,
  });

  // ── Load image & set initial scale to cover the crop square ──────────────
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const minCover = Math.max(
        CROP_SIZE / img.naturalWidth,
        CROP_SIZE / img.naturalHeight,
      );
      setScale(minCover);
      setOffset({ x: 0, y: 0 });
      setImgLoaded(true);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // ── Keep offset inside bounds whenever scale changes ─────────────────────
  const handleScaleChange = useCallback((next: number) => {
    const img = imgRef.current;
    if (!img) return;
    setScale(next);
    setOffset((prev) => clampOffset(next, prev, img.naturalWidth, img.naturalHeight));
  }, []);

  // ── Draw canvas ───────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;

    const ctx = canvas.getContext('2d')!;
    const cx = CANVAS_SIZE / 2;
    const cy = CANVAS_SIZE / 2;

    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    // Draw the image
    const sw = img.naturalWidth * scale;
    const sh = img.naturalHeight * scale;
    ctx.drawImage(img, cx - sw / 2 + offset.x, cy - sh / 2 + offset.y, sw, sh);

    // Dark overlay with square hole
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.52)';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillRect(cx - CROP_SIZE / 2, cy - CROP_SIZE / 2, CROP_SIZE, CROP_SIZE);
    ctx.restore();

    // Square border
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.strokeRect(cx - CROP_SIZE / 2, cy - CROP_SIZE / 2, CROP_SIZE, CROP_SIZE);
    ctx.setLineDash([]);
  }, [imgLoaded, scale, offset]);

  useEffect(() => { draw(); }, [draw]);

  // ── Drag: mouse ───────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { active: true, startMx: e.clientX, startMy: e.clientY, startOx: offset.x, startOy: offset.y };
  };

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragRef.current.active) return;
    const { startMx, startMy, startOx, startOy } = dragRef.current;
    const img = imgRef.current;
    const next = { x: startOx + (e.clientX - startMx), y: startOy + (e.clientY - startMy) };
    setOffset(
      img
        ? clampOffset(scale, next, img.naturalWidth, img.naturalHeight)
        : next,
    );
  }, [scale]);

  const onMouseUp = () => { dragRef.current.active = false; };

  // ── Drag: touch ───────────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    dragRef.current = { active: true, startMx: t.clientX, startMy: t.clientY, startOx: offset.x, startOy: offset.y };
  };

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!dragRef.current.active) return;
    const t = e.touches[0];
    const { startMx, startMy, startOx, startOy } = dragRef.current;
    const img = imgRef.current;
    const next = { x: startOx + (t.clientX - startMx), y: startOy + (t.clientY - startMy) };
    setOffset(
      img
        ? clampOffset(scale, next, img.naturalWidth, img.naturalHeight)
        : next,
    );
  }, [scale]);

  const onTouchEnd = () => { dragRef.current.active = false; };

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const img = imgRef.current;
    const min = img
      ? Math.max(CROP_SIZE / img.naturalWidth, CROP_SIZE / img.naturalHeight)
      : 0.2;
    handleScaleChange(Math.min(Math.max(scale * (1 - e.deltaY * 0.001), min), 5));
  };

  // ── Slider zoom ───────────────────────────────────────────────────────────
  const imgRef2 = imgRef;
  const minScale = imgRef2.current
    ? Math.max(CROP_SIZE / imgRef2.current.naturalWidth, CROP_SIZE / imgRef2.current.naturalHeight)
    : 0.2;
  const maxScale = 5;

  // ── Confirm: export cropped blob ─────────────────────────────────────────
  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img || processing) return;
    setProcessing(true);

    const ratio = OUTPUT_SIZE / CANVAS_SIZE;
    const cx = OUTPUT_SIZE / 2;
    const cy = OUTPUT_SIZE / 2;

    const offscreen = document.createElement('canvas');
    offscreen.width = OUTPUT_SIZE;
    offscreen.height = OUTPUT_SIZE;
    const ctx = offscreen.getContext('2d')!;

    // Square crop: the min-scale guarantee + offset clamping keep the whole
    // output canvas covered by the image, so no transparency/black corners.
    const sw = img.naturalWidth * scale * ratio;
    const sh = img.naturalHeight * scale * ratio;
    ctx.drawImage(img, cx - sw / 2 + offset.x * ratio, cy - sh / 2 + offset.y * ratio, sw, sh);

    offscreen.toBlob(async (rawBlob) => {
      if (!rawBlob) { setProcessing(false); return; }
      try {
        const compressed = await compressImageBlob(rawBlob, { maxSizeKB: 1024, maxDim: 800 });
        onConfirm(compressed);
      } catch {
        onConfirm(rawBlob);
      }
      setProcessing(false);
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">裁剪头像</h3>
          <button
            onClick={onCancel}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-base" />
          </button>
        </div>

        {/* Canvas */}
        <div className="flex justify-center items-center bg-gray-900 py-4">
          {imgLoaded ? (
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              className="rounded-lg cursor-move select-none"
              style={{ touchAction: 'none' }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onWheel={onWheel}
            />
          ) : (
            <div className="w-[300px] h-[300px] flex items-center justify-center">
              <i className="ri-loader-4-line text-white text-3xl animate-spin" />
            </div>
          )}
        </div>

        {/* Zoom slider */}
        <div className="px-5 py-3 flex items-center gap-3 border-b border-gray-100">
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-zoom-out-line text-gray-400 text-base" />
          </div>
          <input
            type="range"
            min={minScale}
            max={maxScale}
            step={0.01}
            value={scale}
            onChange={(e) => handleScaleChange(Number(e.target.value))}
            className="flex-1 accent-teal-500 cursor-pointer"
          />
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-zoom-in-line text-gray-400 text-base" />
          </div>
          <span className="text-xs text-gray-400 w-10 text-right shrink-0">{Math.round(scale * 100)}%</span>
        </div>

        {/* Hint */}
        <p className="text-center text-xs text-gray-400 pt-3 px-5">
          拖动图片调整位置，滚轮或拖动滑块缩放，正方形输出
        </p>

        {/* Actions */}
        <div className="flex gap-3 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="flex-1 h-9 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!imgLoaded || processing}
            className="flex-1 h-9 rounded-lg bg-teal-500 hover:bg-teal-600 text-sm text-white font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {processing ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm" />
                <span>处理中…</span>
              </>
            ) : (
              <>
                <i className="ri-check-line text-sm" />
                <span>确认裁剪</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
